import { Link } from 'react-router-dom';

import { routePaths } from '@/app/router/routePaths';
import type { Tournament } from '@/core/schemas';
import { GoogleIcon } from '@/features/auth/GoogleIcon';
import { useTournament } from '@/features/tournament/useTournament';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { Button } from '@/shared/ui/Button';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';

const DATE_FORMAT = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export function WelcomePage() {
  usePageTitle();
  const { data: tournament, error, isLoading } = useTournament();

  if (isLoading) {
    return <LoadingScreen label="Turnier wird geladen …" />;
  }

  // Bevor die Turnierleitung das Dokument angelegt hat: gleiche Optik, aber
  // ohne konkreten Termin.
  if (tournament === undefined) {
    return (
      <Shell
        kicker="Freiwillige Feuerwehr Ratekau"
        title="Feuerwehr-Dart-Cup"
        subtitle="Wir bereiten den nächsten Turniertag vor."
      >
        <p className="text-fg-muted max-w-prose text-sm">
          {error?.kind === 'not-found'
            ? 'Das Turnier ist noch nicht freigeschaltet — sobald die Turnierleitung den Termin veröffentlicht, findet ihr hier alle Infos.'
            : 'Sobald der nächste Termin freigegeben ist, findet ihr hier alle Infos.'}
        </p>
        <SignInCta />
      </Shell>
    );
  }

  return (
    <Shell
      kicker="Freiwillige Feuerwehr Ratekau"
      title={tournament.settings.general.name}
      subtitle={formatTournamentDate(tournament.settings.general.date)}
    >
      <StatusSection tournament={tournament} />
    </Shell>
  );
}

interface ShellProps {
  kicker: string;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}

function Shell({ kicker, title, subtitle, children }: ShellProps) {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-8 py-8 sm:py-12">
      <header className="flex flex-col gap-3">
        <p className="text-brand text-xs font-semibold tracking-widest uppercase">{kicker}</p>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{title}</h1>
        <p className="text-fg-muted text-lg">{subtitle}</p>
      </header>

      <div className="flex flex-col gap-6">{children}</div>

      <footer className="border-line text-fg-muted mt-4 border-t pt-6 text-sm">
        <p>
          Am Turniertag läuft die Live-Anzeige unter{' '}
          <Link to={routePaths.presentation} className="text-brand underline underline-offset-2">
            /live
          </Link>{' '}
          — ohne Anmeldung, geeignet für Beamer und Bildschirme.
        </p>
      </footer>
    </div>
  );
}

interface StatusSectionProps {
  tournament: Tournament;
}

function StatusSection({ tournament }: StatusSectionProps) {
  const registrationOpen = tournament.settings.registration.isOpen;

  switch (tournament.status) {
    case 'setup':
      return (
        <>
          <StatusCard tone="info" title="Anmeldung öffnet in Kürze">
            Sobald die Turnierleitung die Anmeldung freischaltet, könnt ihr euer Team hier
            registrieren.
          </StatusCard>
          <SignInCta />
        </>
      );

    case 'registration':
      return registrationOpen ? (
        <>
          <StatusCard tone="success" title="Anmeldung ist geöffnet">
            Meldet euer Team mit Namen, Löschzug und Einlaufsong an. Kapitäne bestätigen die
            Ergebnisse am Turniertag direkt am Board.
          </StatusCard>
          <SignInCta />
        </>
      ) : (
        <>
          <StatusCard tone="info" title="Anmeldung derzeit pausiert">
            Die Turnierleitung hat die Anmeldung vorübergehend geschlossen.
          </StatusCard>
          <SignInCta />
        </>
      );

    case 'running':
      return (
        <>
          <StatusCard tone="success" title="Das Turnier läuft">
            Aktuelle Ergebnisse und Tabellen sind öffentlich einsehbar.
          </StatusCard>
          <PublicLinks />
          <SignInCta variant="secondary" />
        </>
      );

    case 'paused':
      return (
        <>
          <StatusCard tone="info" title="Kurze Pause">
            Das Turnier ist gerade angehalten — es geht in Kürze weiter.
          </StatusCard>
          <PublicLinks />
        </>
      );

    case 'completed':
      return (
        <>
          <StatusCard tone="success" title="Turnier beendet">
            Alle Ergebnisse bleiben verfügbar.
          </StatusCard>
          <PublicLinks />
        </>
      );

    case 'archived':
      return (
        <StatusCard tone="muted" title="Vergangenes Turnier">
          Die Ergebnisse dieses Turniers sind archiviert.
        </StatusCard>
      );
  }
}

type CardTone = 'success' | 'info' | 'muted';

interface StatusCardProps {
  tone: CardTone;
  title: string;
  children: React.ReactNode;
}

const TONE_STYLES: Readonly<Record<CardTone, string>> = {
  success: 'border-brand/40 bg-brand/5',
  info: 'border-line bg-surface',
  muted: 'border-line bg-surface',
};

function StatusCard({ tone, title, children }: StatusCardProps) {
  return (
    <section className={`rounded-xl border p-5 sm:p-6 ${TONE_STYLES[tone]}`}>
      <h2 className="mb-1.5 text-lg font-semibold">{title}</h2>
      <p className="text-fg-muted text-sm">{children}</p>
    </section>
  );
}

function SignInCta({ variant = 'primary' }: { variant?: 'primary' | 'secondary' } = {}) {
  return (
    <div className="flex flex-col items-start gap-3">
      <p className="text-fg-muted text-sm">
        Die Anmeldung ist nur für <span className="text-fg font-medium">Teamkapitäne</span>.
      </p>
      <Link to={routePaths.login} className="inline-block">
        <Button size="lg" variant={variant} icon={<GoogleIcon />}>
          Mit Google anmelden
        </Button>
      </Link>
    </div>
  );
}

function PublicLinks() {
  return (
    <nav className="grid gap-3 sm:grid-cols-3">
      <PublicLink to={routePaths.tournament.schedule} label="Spielplan" />
      <PublicLink to={routePaths.tournament.groups} label="Gruppen" />
      <PublicLink to={routePaths.tournament.knockout} label="KO-Runde" />
    </nav>
  );
}

function PublicLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="border-line bg-surface hover:border-brand/50 hover:bg-surface-raised flex items-center justify-between rounded-lg border px-4 py-3 text-sm font-medium transition-colors"
    >
      <span>{label}</span>
      <span aria-hidden className="text-fg-muted">
        →
      </span>
    </Link>
  );
}

function formatTournamentDate(isoDate: string): string {
  // Ohne Zeit-Anteil wird das Datum als UTC-Mitternacht geparst; wir wollen aber
  // den lokal gemeinten Tag anzeigen, deshalb explizit lokal konstruieren.
  const [yearRaw, monthRaw, dayRaw] = isoDate.split('-');
  const year = Number.parseInt(yearRaw ?? '', 10);
  const month = Number.parseInt(monthRaw ?? '', 10);
  const day = Number.parseInt(dayRaw ?? '', 10);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return isoDate;
  }
  return DATE_FORMAT.format(new Date(year, month - 1, day));
}
